import type { IncomingMaterialsTranslation } from './types'

/**
 * Arabic translations for Incoming Materials domain
 */
export const incoming_materialsTranslationAr: IncomingMaterialsTranslation = {
  // Page/Section level
  title: 'المواد الواردة',
  subtitle: 'إدارة أوامر الشراء والموردين واستلام البضائع',
  description: 'إدارة المواد الواردة في النظام',

  // Purchase Order
  purchaseOrder: 'أمر الشراء',
  poNumber: 'رقم أمر الشراء',
  poDate: 'تاريخ أمر الشراء',
  poDetails: 'تفاصيل أمر الشراء',
  poLinesTitle: 'بنود الطلب',
  poLinesHint: 'أضف مواد إلى أمر الشراء هذا',

  // PO Status
  poStatus: {
    draft: 'مسودة',
    issued: 'صادر',
    closed: 'مغلق',
    cancelled: 'ملغى',
    pending: 'قيد الانتظار',
    completed: 'مكتمل',
  },
  factory: 'المصنع',
  materialGroup: 'مجموعة المواد',

  // Receipt Status
  receiptStatus: {
    pending: 'في انتظار الاستلام',
    partial: 'مستلم جزئياً',
    complete: 'مكتمل',
  },

  // Line Status
  status: {
    pending: 'معلق',
    partial: 'جزئي',
    accepted: 'مقبول',
    rejected: 'مرفوض',
    disputed: 'متنازع عليه',
  },

  // Create/Actions
  createTitle: 'إنشاء أمر شراء',
  createHint: 'إنشاء أمر شراء جديد للمواد الواردة',
  createPurchaseOrder: 'إنشاء أمر شراء',
  createPOHint: 'إضافة أمر شراء جديد لتتبع المواد الواردة',
  createSupplier: 'إنشاء مورد',
  createMaterial: 'إنشاء مادة',
  createMaterialHint: 'إضافة مادة جديدة إلى الكتالوج',
  createFirstPO: 'إنشاء أول أمر شراء',
  receiveGoods: 'استلام البضائع',
  receiveHint: 'تسجيل استلام البضائع مقابل أمر الشراء',
  confirmReceipt: 'تأكيد الاستلام',

  // Lines
  linesTitle: 'بنود الطلب',
  linesHint: 'المواد المراد استلامها',
  addLine: 'إضافة بند',
  removeLine: 'إزالة بند',
  receiveLines: 'استلام البنود',
  receiveLinesHint: 'أدخل الكميات المستلمة لكل بند',
  noPoLines: 'لا توجد بنود في أمر الشراء هذا',

  // Supplier
  supplier: 'المورد',
  supplierName: 'اسم المورد',
  supplierCode: 'كود المورد',
  taxId: 'الرقم الضريبي',
  noSupplier: 'لم يتم اختيار مورد',

  // Material
  materialName: 'اسم المادة',
  materialCode: 'كود المادة',
  materialDescription: 'وصف المادة',

  // Fields
  fields: {
    supplier: 'المورد',
    purchaseOrder: 'أمر الشراء',
    purchaseOrderLine: 'بند أمر الشراء',
    material: 'المادة',
    quantity: 'الكمية',
    status: 'الحالة',
    receivedAt: 'وقت الاستلام',
    driverName: 'اسم السائق',
    driverPhone: 'هاتف السائق',
    vehiclePlate: 'لوحة المركبة',
    supplierDeliveryNoteNo: 'رقم إشعار التسليم',
    notes: 'ملاحظات',
  },

  // Placeholders
  placeholders: {
    selectSupplier: 'اختر مورداً',
    supplierName: 'أدخل اسم المورد',
    selectPurchaseOrder: 'اختر أمر شراء',
    selectPurchaseOrderLine: 'اختر بند أمر الشراء',
    selectMaterial: 'اختر مادة',
    materialName: 'أدخل اسم المادة',
    materialDescription: 'أدخل وصف المادة',
    deliveryNote: 'أدخل رقم إشعار التسليم',
    driverName: 'أدخل اسم السائق',
    driverPhone: 'أدخل هاتف السائق',
    vehiclePlate: 'أدخل لوحة المركبة',
    notes: 'أدخل ملاحظات',
  },

  // Validation
  validation: {
    supplierNameRequired: 'اسم المورد مطلوب',
    supplierCodeRequired: 'كود المورد مطلوب',
  },

  // Table
  table: {
    materials: 'المواد',
    receiptStatus: 'حالة الاستلام',
    moreItems: 'عناصر إضافية',
    showing: 'عرض {{start}}-{{end}} من {{total}}',
    perPage: 'لكل صفحة',
  },

  filters: {
    title: 'الفلاتر',
    all: 'الكل',
    apply: 'تطبيق',
    clearAll: 'مسح الكل',
    status: 'الحالة',
    supplier: 'المورد',
    material: 'المادة',
    supplierSearchPlaceholder: 'ابحث عن مورد...',
    materialSearchPlaceholder: 'ابحث عن مادة...',
    loading: 'جارٍ التحميل...',
    noSuppliers: 'لم يتم العثور على موردين',
    noMaterials: 'لم يتم العثور على مواد',
  },

  // Other
  expectedDeliveryDate: 'تاريخ التسليم المتوقع',
  uom: 'وحدة القياس',
  defaultUom: 'وحدة',
  overallProgress: 'التقدم الإجمالي',
  received: 'مستلم',
  complete: 'مكتمل',
  receiptsHistory: 'سجل الاستلام',
  notesPlaceholder: 'أضف ملاحظات حول هذا الاستلام...',
  searchPOs: 'ابحث في أوامر الشراء...',
  noPurchaseOrders: 'لا توجد أوامر شراء',
  noMatchingPOs: 'لا توجد أوامر شراء مطابقة',
  totalPOs: 'إجمالي أوامر الشراء',
  filteredPOs: 'أوامر الشراء المفلترة',
  showingPOs: 'عرض أوامر الشراء',
  items: 'عناصر',
  receipts: 'إيصالات',
  receivedQty: 'الكمية المستلمة: {{qty}} {{uom}}',
  orderedVsReceived: 'المطلوب مقابل المستلم',
  selectedSupplier: 'المورد المحدد',

  // Actions (legacy)
  actions: {
    create: 'إنشاء',
    edit: 'تعديل',
    delete: 'حذف',
    view: 'عرض',
    save: 'حفظ',
    cancel: 'إلغاء',
  },

  // Form (legacy)
  form: {
    name: 'الاسم',
    description: 'الوصف',
    status: 'الحالة',
  },

  // Messages
  messages: {
    created: 'تم إنشاء المادة الواردة بنجاح',
    updated: 'تم تحديث المادة الواردة بنجاح',
    deleted: 'تم حذف المادة الواردة بنجاح',
    error: 'حدث خطأ',
  },

  // Inbound Shipments
  kioskInbound: {
    title: 'هل تقوم بالتوصيل إلينا؟',
    subtitle: 'سجّل هنا إذا لم تتمكن البوابة من قراءة رمز QR أو لوحة سيارتك.',
    plateLabel: 'رقم لوحة سيارتك',
    plateDigitsPlaceholder: '1234',
    plateLettersPlaceholder: 'ABC',
    poNumberLabel: 'رقم أمر الشراء',
    poNumberPlaceholder: 'مثال: 4500000219',
    lineNumberLabel: 'رقم البند',
    lineNumberPlaceholder: 'مثال: 0010',
    scanHint: 'لديك رمز QR على أوراقك؟ امسحه لتعبئة هذه الحقول.',
    scanNotRecognised: 'تعذّرت قراءة هذا الرمز. حاول مرة أخرى أو اكتب الأرقام أدناه.',
    findLoads: 'متابعة',
    remainingOf: 'متبقٍ {{remaining}} من {{ordered}} {{uom}}',
    register: 'تسجيل وصولي',
    registering: 'جارٍ التسجيل…',
    startOver: 'البدء من جديد',
    successTitle: 'تم تسجيلك',
    successHint: 'خذ القسيمة وتوجّه إلى البوابة. ستُفتح عند قراءة لوحتك.',
    printAgain: 'طباعة القسيمة مرة أخرى',
    poNotFound: 'لا يوجد أمر شراء بهذا الرقم.',
    lineNotOnPo: 'البند {{line}} غير موجود في أمر الشراء هذا.',
    lineFullyReceived: 'البند {{line}} تم توصيله بالكامل.',
  },

  inboundShipments: {
    title: 'الشحنات الواردة',
    subtitle: 'تتبع وإدارة وصول الشحنات الواردة وعمليات الميزان',
    searchPlaceholder: 'ابحث برقم الشحنة أو لوحة السيارة أو رقم أمر الشراء...',
    noShipments: 'لا توجد شحنات واردة',
    noMatchingShipments: 'لا توجد شحنات تطابق بحثك أو تصفيتك',
    totalShipments: 'إجمالي الشحنات',
    showingShipments: 'عرض الشحنات',
    shipmentNumber: 'رقم الشحنة',
    vehiclePlate: 'لوحة السيارة',
    driver: 'السائق',
    arrivedAt: 'وقت الوصول',
    grossWeight: 'الوزن الإجمالي (كغ)',
    tareWeight: 'وزن الحاوية (كغ)',
    netWeight: 'الوزن الصافي (كغ)',
    firstWeightAt: 'وقت الوزن الأول',
    secondWeightAt: 'وقت الوزن الثاني',
    grnNumber: 'رقم إيصال البضاعة',
    sapMaterialDocument: 'مستند مواد SAP',
    sapSyncStatus: 'حالة مزامنة SAP',
    purchaseOrder: 'أمر الشراء',
    supplier: 'المورد',
    gate: 'البوابة',
    statusFilter: {
      all: 'الكل',
      arrived: 'وصل',
      first_weight_done: 'الوزن الأول مكتمل',
      unloaded: 'تم التفريغ',
      second_weight_done: 'الوزن الثاني مكتمل',
      grn_posted: 'تم ترحيل الإيصال',
      grn_skipped: 'تم تخطي الترحيل',
    },
    sapSyncStatusLabel: {
      pending: 'معلق',
      first_weight_synced: 'تمت مزامنة الوزن الأول',
      second_weight_synced: 'تمت مزامنة الوزن الثاني',
      grn_synced: 'تمت مزامنة الإيصال',
      failed: 'فشل',
    },
    filters: {
      title: 'الفلاتر',
      all: 'الكل',
      apply: 'تطبيق',
      clearAll: 'مسح الكل',
      status: 'الحالة',
      supplier: 'المورد',
      material: 'المادة',
      supplierSearchPlaceholder: 'ابحث عن مورد...',
      materialSearchPlaceholder: 'ابحث عن مادة...',
      loading: 'جارٍ التحميل...',
      noSuppliers: 'لم يتم العثور على موردين',
      noMaterials: 'لم يتم العثور على مواد',
      sapSyncStatus: 'مزامنة SAP',
      grnStatus: 'إيصال البضاعة',
      weightStatus: 'الوزن',
    },
    grnFilter: {
      all: 'كل الإيصالات',
      posted: 'تم ترحيل الإيصال',
      not_posted: 'بدون إيصال',
    },
    weightFilter: {
      all: 'كل الأوزان',
      gross_recorded: 'تم تسجيل الإجمالي',
      net_recorded: 'تم تسجيل الصافي',
      missing_weight: 'وزن مفقود',
    },
    columns: {
      shipmentNumber: 'رقم الشحنة',
      vehiclePlate: 'لوحة السيارة',
      purchaseOrder: 'رقم أمر الشراء',
      material: 'المادة',
      status: 'الحالة',
      grossWeight: 'إجمالي (كغ)',
      netWeight: 'صافي (كغ)',
      sapSyncStatus: 'مزامنة SAP',
      grnNumber: 'رقم الإيصال',
      arrivedAt: 'وقت الوصول',
    },
    detail: {
      title: 'تفاصيل الشحنة',
      overview: 'نظرة عامة',
      timeline: 'الخط الزمني',
      shipmentInfo: 'معلومات الشحنة',
      weightInfo: 'معلومات الوزن',
      syncInfo: 'مزامنة SAP',
      driverName: 'اسم السائق',
      edit: 'تعديل',
      save: 'حفظ',
      cancel: 'إلغاء',
      departedAt: 'وقت المغادرة',
      factory: 'المصنع',
      storageLocation: 'موقع التخزين',
      poNumber: 'رقم أمر الشراء',
      sapPoNumber: 'رقم أمر SAP',
      sapItemNumber: 'بند SAP',
      material: 'المادة',
      orderedQuantity: 'الكمية المطلوبة',
      ticketNumber: 'رقم التذكرة',
      firstWeightSyncedAt: 'وقت مزامنة الوزن الأول',
      secondWeightSyncedAt: 'وقت مزامنة الوزن الثاني',
      grnSyncedAt: 'وقت مزامنة الإيصال',
      error: 'الخطأ',
      success: 'نجاح',
      failed: 'فشل',
      timelineEvents: 'أحداث',
      noTimeline: 'لا توجد أحداث في الخط الزمني بعد',
      timelineError: 'فشل تحميل الخط الزمني',
      refreshTimeline: 'تحديث الخط الزمني',
      timelineStep: 'الخطوة',
      timelineStatus: 'الحالة',
      weightKg: 'الوزن',
      netWeightKg: 'الوزن الصافي',
      timelineActions: {
        CREATED: 'تم الإنشاء',
        WEIGHBRIDGE_TRIGGERED: 'تدفق الميزان',
        GATE_OPENED: 'فتح البوابة',
        INBOUND_FIRST_WEIGHT: 'الوزن الأول',
        INBOUND_SECOND_WEIGHT: 'الوزن الثاني',
        INBOUND_GRN: 'إيصال البضاعة',
      },
      timelineEmpty: 'لا توجد أحداث في المخطط الزمني بعد',
      timelineLoadError: 'تعذر تحميل المخطط الزمني',
      timelineSuccess: 'نجاح',
      timelineFailed: 'فشل',
      timelineRefresh: 'تحديث',
    },
  },
}
